/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { useDispatch } from 'react-redux';
import { ActionButton, BrandColors, Modal } from '@blend/enterprise-components';
import { AxiosError } from 'axios';
import { setRoomError } from '../../../../../actions/roomActions';
import { ModalContent } from '../../../../../components/common/enterprise/ModalContent/ModalContent';
import { notaryAPI } from '../../../../../api/notary';
import { useParticipants } from '../../../../../hooks/useParticipants';

const styles = StyleSheet.create({
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 14,
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
  },
  submitWrapper: {
    marginLeft: 8,
  },
});

export namespace RemoveParticipantModal {
  export interface Props {
    isOpen: boolean;
    participantId: string;
    roomId: string;
    headerText: string;
    subHeaderText?: string;
    handleClose: () => void;
  }
}

export const RemoveParticipantModal: React.FC<RemoveParticipantModal.Props> = ({
  isOpen,
  roomId,
  participantId,
  headerText,
  subHeaderText,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const { dispatchParticipants } = useParticipants(roomId);
  const removeParticipant = async () => {
    try {
      setSubmitting(true);
      await notaryAPI.rooms.deleteParticipant(roomId, participantId);
      await dispatchParticipants();
      handleClose();
      setSubmitting(false);
    } catch (error) {
      setRoomError({ message: (error as AxiosError<{ message?: string }>)?.response?.data?.message, hasError: true })(
        dispatch,
      );
      handleClose();
      setSubmitting(false);
    }
  };
  return (
    <Modal data-it="RemoveParticipantModal" contentLabel={headerText} handleClose={handleClose} isOpen={isOpen}>
      <ModalContent handleClose={handleClose} headerText={headerText} subHeaderText={subHeaderText}>
        <div className={css(styles.footerBar)}>
          <ActionButton
            data-dd-privacy="allow"
            data-it="RemoveParticipantModal-cancel"
            label="Cancel"
            onClick={handleClose}
            theme="secondary"
          />
          <div className={css(styles.submitWrapper)}>
            <ActionButton
              data-dd-privacy="allow"
              disabled={submitting}
              data-it="RemoveParticipantModal-remove"
              label="Remove"
              theme="warning"
              onClick={removeParticipant}
            />
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};
